import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, RefreshCw, RotateCcw, MessageCircle, MapPin, Briefcase, GraduationCap, Heart, Instagram, Star, Ruler, Calendar, ChevronLeft, BarChart3 } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import BottomNav from '@/components/BottomNav';

function waHref(num) {
  if (!num) return null;
  const clean = String(num).replace(/[^0-9]/g, '');
  return clean ? `https://wa.me/${clean}` : null;
}

function PhotoCarousel({ photos, name, testIdPrefix }) {
  const [idx, setIdx] = useState(0);
  const list = Array.isArray(photos) && photos.length > 0 ? photos : [null];
  const url = list[idx];
  return (
    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br from-[#F5E7EA] to-[#E8D5D9]" data-testid={`${testIdPrefix}-carousel`}>
      {url ? (
        <img src={url} alt={name || 'match'} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#D98A94]/40 text-6xl font-bold">
          {(name || '?').slice(0, 1).toUpperCase()}
        </div>
      )}
      {list.length > 1 && (
        <>
          <div className="absolute top-2 left-2 right-2 flex gap-1">
            {list.map((photo, i) => (
              <div key={photo || `photo-slot-${i}`} className={`flex-1 h-1 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
          <button
            onClick={() => setIdx((idx - 1 + list.length) % list.length)}
            aria-label="prev photo"
            className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm"
            data-testid={`${testIdPrefix}-prev`}
          >‹</button>
          <button
            onClick={() => setIdx((idx + 1) % list.length)}
            aria-label="next photo"
            className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm"
            data-testid={`${testIdPrefix}-next`}
          >›</button>
        </>
      )}
    </div>
  );
}

function MatchCard({ m, isID, idx }) {
  const p = m.profile;
  const wa = waHref(p.whatsapp);
  const longReason = isID ? m.long_reason_id : m.long_reason_en;
  const facts = [
    p.age && { icon: Calendar, label: `${p.age} ${isID ? 'tahun' : 'yo'}` },
    p.domicile && { icon: MapPin, label: p.domicile },
    p.occupation && { icon: Briefcase, label: p.occupation },
    p.education && { icon: GraduationCap, label: p.education },
    p.body_height && { icon: Ruler, label: `${p.body_height} cm` },
    p.mbti && { icon: Star, label: p.mbti },
  ].filter(Boolean);

  return (
    <div className="glass-card rounded-2xl overflow-hidden border t-border" data-testid={`ai-match-card-${idx}`}>
      <div className="grid md:grid-cols-[minmax(220px,300px)_1fr] gap-0">
        <div className="p-3">
          <PhotoCarousel photos={p.photos} name={p.name} testIdPrefix={`ai-match-${idx}-photo`} />
        </div>
        <div className="p-4 sm:p-5 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#C0727C] uppercase tracking-widest mb-1">
                <Sparkles className="w-3 h-3" />
                <span>{isID ? 'Cocok #' : 'Match #'}{idx + 1}</span>
                {typeof m.score === 'number' && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-md bg-[#D98A94]/10 t-text-secondary">
                    {isID ? 'Skor' : 'Score'}: {m.score}
                  </span>
                )}
              </div>
              <h3 className="text-xl sm:text-2xl font-bold t-text truncate" data-testid={`ai-match-name-${idx}`}>
                {p.name}
              </h3>
              {p.category && (
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-[#D98A94]/10 text-[#C0727C] text-[11px] font-semibold">
                  {p.category}
                </span>
              )}
            </div>
          </div>

          {facts.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {facts.map((f, fi) => (
                <span key={f.label + fi} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/60 border t-border text-[11px] t-text-secondary">
                  <f.icon className="w-3 h-3" />
                  <span className="truncate max-w-[140px]">{f.label}</span>
                </span>
              ))}
            </div>
          )}

          {p.bio && (
            <p className="text-xs t-text-secondary italic line-clamp-3">{`"${p.bio}"`}</p>
          )}

          <div className="rounded-xl bg-[#D98A94]/6 border border-[#D98A94]/25 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#C0727C] uppercase tracking-widest mb-1.5">
              <Sparkles className="w-3 h-3" />
              {isID ? 'Kenapa cocok?' : 'Why we matched'}
            </div>
            <p className="text-xs t-text leading-relaxed whitespace-pre-line" data-testid={`ai-match-reason-${idx}`}>
              {longReason}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {wa ? (
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ai-match-wa-${idx}`}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1FBA57] text-white font-bold text-sm shadow-md hover:shadow-lg transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                <span>{isID ? 'Chat WhatsApp' : 'Chat on WhatsApp'}</span>
              </a>
            ) : (
              <span className="text-[11px] t-text-muted italic">
                {isID ? 'Nomor WhatsApp belum diisi.' : 'WhatsApp number missing.'}
              </span>
            )}
            {p.instagram && (
              <a
                href={`https://instagram.com/${p.instagram.replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ai-match-ig-${idx}`}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full border t-border t-text-secondary hover:bg-white/60 text-xs font-semibold"
              >
                <Instagram className="w-3.5 h-3.5" />
                <span>{p.instagram}</span>
              </a>
            )}
          </div>

          {p.whatsapp && (
            <div className="text-[10px] t-text-muted font-mono pt-1">
              {p.whatsapp}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AIMatchPage() {
  const { token, claimedProfile, API } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const isID = lang !== 'en';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyCount, setHistoryCount] = useState(0);
  const [error, setError] = useState('');

  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const generate = useCallback(async (reset = false) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/members/ai-match/generate`, { reset }, headers);
      setData(res.data);
      setHistoryCount(res.data.total_shown || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || (isID ? 'Gagal memuat.' : 'Failed to load.'));
    }
    setLoading(false);
  }, [API, headers, isID]);

  useEffect(() => {
    // Fetch history count on mount
    axios.get(`${API}/members/ai-match/history-count`, headers)
      .then(r => setHistoryCount(r.data.total_shown || 0))
      .catch(() => {});
    generate(false);
  }, []);

  const noProfile = !claimedProfile?.id;

  return (
    <div className="min-h-screen t-bg pb-32">
      <div className="max-w-4xl mx-auto px-4 py-5 sm:py-8">
        <button
          onClick={() => navigate('/browse')}
          className="inline-flex items-center gap-1 text-xs t-text-secondary hover:t-text mb-4"
          data-testid="ai-match-back"
        >
          <ChevronLeft className="w-4 h-4" />
          {isID ? 'Kembali ke Browse' : 'Back to Browse'}
        </button>

        <div className="glass-card rounded-2xl p-5 sm:p-6 mb-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D98A94] to-[#C0727C] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold t-text truncate">
                  {isID ? 'AI Match Pribadi' : 'Personal AI Match'}
                </h1>
                <p className="text-xs t-text-secondary">
                  {isID
                    ? '3 profil paling cocok untuk kamu berdasarkan usia, domisili, tujuan, dan hobi.'
                    : '3 profiles hand-picked for you based on age, location, intent, and hobbies.'}
                </p>
              </div>
            </div>
            {claimedProfile?.category && (
              <Link
                to={`/kategori/${claimedProfile.category.toLowerCase().replace(/\s+/g, '-')}`}
                data-testid="ai-match-stats-link"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#D98A94]/30 bg-[#D98A94]/10 hover:bg-[#D98A94]/20 text-[#C0727C] text-xs font-bold transition-colors flex-shrink-0"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                <span>{isID ? 'Statistik Komunitas' : 'Deep Stats'}</span>
              </Link>
            )}
          </div>

          {noProfile ? (
            <div className="mt-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                {isID
                  ? 'Kamu harus klaim profil member dulu untuk memakai AI Match. Buka halaman Profil untuk klaim.'
                  : 'You need to claim a member profile before using AI Match. Go to Profile to claim.'}
              </p>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                onClick={() => generate(false)}
                disabled={loading}
                data-testid="ai-match-generate-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D98A94] hover:bg-[#C0727C] text-white text-sm font-semibold disabled:opacity-60 shadow-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {isID ? 'Generate 3 Match Baru' : 'Generate 3 New Matches'}
              </button>
              <button
                onClick={() => generate(true)}
                disabled={loading}
                data-testid="ai-match-reset-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border t-border t-text-secondary hover:bg-white/60 text-xs font-semibold disabled:opacity-60"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {isID ? 'Mulai Ulang' : 'Restart'}
              </button>
              <span className="ml-auto text-[11px] t-text-muted">
                {isID ? 'Sudah dilihat' : 'Seen'}: {historyCount}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800 mb-4" data-testid="ai-match-error">
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-12 t-text-muted text-sm" data-testid="ai-match-loading">
            {isID ? 'Menghitung profil paling cocok…' : 'Computing best matches…'}
          </div>
        )}

        {!loading && data && data.exhausted && (
          <div className="glass-card rounded-2xl p-8 text-center" data-testid="ai-match-exhausted">
            <Heart className="w-12 h-12 mx-auto mb-3 text-[#D98A94]/60" />
            <h3 className="font-bold t-text mb-1">
              {isID ? 'Semua kandidat sudah kamu lihat' : 'You\'ve seen every candidate'}
            </h3>
            <p className="text-sm t-text-secondary mb-4">
              {isID
                ? 'Klik "Mulai Ulang" untuk memutar kolam profil dari awal.'
                : 'Tap "Restart" to reshuffle the candidate pool from the beginning.'}
            </p>
          </div>
        )}

        {!loading && data && !data.exhausted && data.matches?.length > 0 && (
          <div className="space-y-4" data-testid="ai-match-results">
            {data.matches.map((m, i) => (
              <MatchCard key={m.profile.id} m={m} idx={i} isID={isID} />
            ))}
            {typeof data.remaining_pool === 'number' && data.remaining_pool > 0 && (
              <p className="text-center text-[11px] t-text-muted pt-2">
                {isID
                  ? `Masih ada ${data.remaining_pool} kandidat lagi di kolam.`
                  : `${data.remaining_pool} more candidate(s) still in the pool.`}
              </p>
            )}
          </div>
        )}
      </div>

      <BottomNav active="ai-match" />
    </div>
  );
}
